import React, { useEffect, useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import * as UIcons from '@iconscout/react-unicons';
import { withTranslation } from 'react-i18next';

export default withTranslation()(function LogoUploader({
  t,
  info,
  title,
  name,
  defaultPic,
  id = Math.random()
}) {
  const { setValue, reset } = useFormContext();

  const [preview, setPreview] = useState(null);
  const [isValidDefaultPic, setIsValidDefaultPic] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  // Validate defaultPic URL
  useEffect(() => {
    if (!defaultPic) return setIsValidDefaultPic(false);

    const img = new Image();
    img.onload = () => setIsValidDefaultPic(true);
    img.onerror = () => setIsValidDefaultPic(false);
    img.src = defaultPic;
  }, [defaultPic]);

  // Handle normal file upload + drag and drop
  const handleFile = useCallback(
    (fileList) => {
      const file = fileList?.[0];
      if (!file) return;

      if (!file.type.includes('image')) {
        setPreview('not_an_image');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      setValue(name, file);
    },
    [setValue, name]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  // Sync form reset with info props
  useEffect(() => {
    reset(info);
    reset({ [name]: info[name] });
  }, [info, reset, name]);

  return (
    <form className="w-full bg-white p-8 mb-5 rounded-xl shadow-md">
      <div className="flex justify-between w-full mb-5">
        <h1 className="text-xl font-bold text-secondary">{title}</h1>
      </div>

      <div className="w-full grid md:grid-cols-2 md:gap-8">

        {/* Default Picture */}
        <div className='border rounded-md flex items-center justify-center p-2'>
          {defaultPic ? (
            isValidDefaultPic ? (
              <img
                src={defaultPic}
                alt="default"
                className="max-w-[180px] max-h-[180px] object-cover mb-5 rounded-md"
              />
            ) : (
              <p className="text-danger text-center self-center mb-5">image is not valid</p>
            )
          ) : null}
        </div>

        <p className="w-full md:p-3 text-placeholder mb-7 md:mb-0">
          {t('settings.business_logo.label.1')}
          <b className="font-medium text-secondary">
            {' '}{t('settings.business_logo.label.2')}
          </b>
          {' '}{t('settings.business_logo.label.3')}
        </p>

        {/* Upload Area */}
        <div className="w-full md:p-3">
          <div
            className={`p-10 w-full text-center rounded-xl border-2 border-dashed flex flex-col justify-center items-center cursor-pointer transition 
              ${dragOver ? 'border-primary bg-blue-50' : 'border-primary'}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById(id)?.click()}
          >
            <UIcons.UilPaperclip className="text-primary mb-2" />
            <span className="font-medium text-primary">
              {t('settings.business_logo.btn_text')}
            </span>
            <span className="text-xs text-placeholder">(Drag & Drop or Click)</span>
          </div>

          <input
            type="file"
            name={name}
            id={id}
            className="hidden"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files)}
          />
        </div>

        {/* Preview */}
        <div className="w-full m-2">
          {preview ? (
            preview !== 'not_an_image' ? (
              <img
                src={preview}
                alt="preview"
                className="max-w-[180px] max-h-[180px] object-cover mt-2 rounded-md"
              />
            ) : (
              <p className="text-danger">Please upload an image file only</p>
            )
          ) : null}
        </div>

      </div>
    </form>
  );
});
